/**
 * ConflictBanner — inline 409 banner, never a modal (spec.md § Editor /
 * Conflict, § UI & design / Save state machine).
 *
 * Exact copy from the spec:
 *   "This pad changed. An agent saved version N while you were writing — your
 *    edits weren't overwritten."
 * with a "Reload theirs" action. The accent tint is reserved for the conflict
 * banner per § Visual design system, applied here via the `accent` token at low
 * opacity (no hard-coded colors).
 *
 * The "Reload theirs" flow (copy local body → toast → replace content) lives in
 * the editor; this component only renders the banner and invokes the callback.
 */
import { TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";

export interface ConflictBannerProps {
  /** The server version the conflicting save reported (the "version N"). */
  serverVersion: number;
  /** Invoked when the user clicks "Reload theirs". */
  onReloadTheirs: () => void;
}

export function ConflictBanner({ serverVersion, onReloadTheirs }: ConflictBannerProps): ReactNode {
  return (
    <div
      role="alert"
      data-testid="conflict-banner"
      className="flex items-start gap-3 border-b border-accent/30 bg-accent/10 px-6 py-3 text-text"
    >
      <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-accent" />
      <p className="flex-1 font-sans text-[14px] leading-snug text-text-2">
        This pad changed. An agent saved version {serverVersion} while you were writing — your edits
        weren&rsquo;t overwritten.
      </p>
      <button
        type="button"
        onClick={onReloadTheirs}
        className="shrink-0 rounded-[9px] border border-accent/40 px-3 py-1.5 font-mono text-[13px] font-medium text-accent transition-colors hover:bg-accent/15 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/40 active:translate-y-px"
      >
        Reload theirs
      </button>
    </div>
  );
}
